/**
 * A stored Amazon SEARCH link is a query fixed the day it was written, from
 * whatever the product was called then. A lineup change or a translation can
 * leave it on a product it never named: on 2026-10-31 the fr-CA card "Table à
 * rallonges Kilo d'EQ3" sent shoppers to a search for "The Brick Dalton Drop
 * Leaf Dining Table". A search link names its product only when its words
 * carry the product's maker (or, with no maker known, at least two of the
 * product's own words); otherwise the card searches for its own title, which
 * is what it does when no link is stored at all.
 *
 * Pure and client-safe.
 */

const AMAZON_HOST_RE = /(^|\.)amazon\.[a-z.]+$/i;

const words = (text: string | null | undefined): string[] =>
  (text ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);

/** The search terms of an Amazon search URL, or null when the URL is not one. */
export function amazonSearchTerms(url: string): string | null {
  try {
    const parsed = new URL(url);
    if (!AMAZON_HOST_RE.test(parsed.hostname)) return null;
    const terms = parsed.searchParams.get('k') ?? parsed.searchParams.get('field-keywords');
    return terms && terms.trim() ? terms : null;
  } catch {
    return null;
  }
}

/** Does this search's query name this product? */
export function searchNamesProduct(terms: string, product: { title?: string | null; brandName?: string | null }): boolean {
  const query = new Set(words(terms));
  const maker = words(product.brandName).filter((word) => word.length >= 2);
  if (maker.length > 0) return maker.some((word) => query.has(word));
  const own = new Set(words(product.title).filter((word) => word.length >= 3));
  let shared = 0;
  for (const word of own) if (query.has(word)) shared += 1;
  return shared >= 2;
}

/**
 * The search link to use for this product: the stored one when it names the
 * product, else the same Amazon store searched for the product's own title.
 * Any URL that is not an Amazon search comes back unchanged.
 */
export function amazonSearchForProduct(
  url: string,
  product: { title?: string | null; brandName?: string | null },
): string {
  const terms = amazonSearchTerms(url);
  const title = product.title?.trim();
  if (!terms || !title || searchNamesProduct(terms, product)) return url;
  try {
    const { origin } = new URL(url);
    return `${origin}/s?k=${encodeURIComponent(title)}`;
  } catch {
    return url;
  }
}
